import React, { Dispatch, SetStateAction, useState } from 'react';
import { useQuery } from '@common/hooks';
import { ErrorHandler, TableTransfer } from '@common/components';
import {
  SELECT_ORGANIZATION_UNIT_QUERY,
  SelectOrganizationUnitType,
} from '@institutions/graphql';
import { TableColumnType } from 'antd';
import { SelectOrganizationUnitsQueryCgResponse } from '@common/graphql';

type NormalizedDataType = {
  id: string;
  key: string;
  facility: string;
  unitName: string;
  costCenter: string;
};

const { filterOptionForColumns } = TableTransfer;

const sortBy = <T extends Record<string, string>>(key: keyof T) => {
  return (a: T, b: T) => a[key].localeCompare(b[key]);
};

const normalizeData = (
  data: SelectOrganizationUnitsQueryCgResponse | undefined
): NormalizedDataType[] =>
  (data?.organizationUnits || []).map((organizationUnit) => ({
    id: organizationUnit.id,
    key: organizationUnit.id,
    facility: organizationUnit.organizationBusiness.name,
    unitName: organizationUnit.name,
    costCenter: organizationUnit.costCenter || '',
  }));

const tableColumns: TableColumnType<NormalizedDataType>[] = [
  {
    dataIndex: 'unitName',
    key: 'unitName',
    title: 'Unit Name',
    sorter: sortBy('unitName'),
  },
  {
    dataIndex: 'costCenter',
    key: 'costCenter',
    title: 'Cost Center',
    sorter: sortBy('costCenter'),
  },
  {
    dataIndex: 'facility',
    key: 'facility',
    title: 'Facility',
    sorter: sortBy('facility'),
  },
];

export function IncentivesUpdateSelectUnitsStep({
  onOrgUnitsChange,
  orgUnits = [],
}: {
  orgUnits: SelectOrganizationUnitType[];
  onOrgUnitsChange: Dispatch<SetStateAction<SelectOrganizationUnitType[]>>;
}) {
  const [targetKeys, setTargetKeys] = useState(orgUnits.map(({ id }) => id));

  const { loading, error, data } = useQuery(SELECT_ORGANIZATION_UNIT_QUERY, {
    variables: {
      input: {
        scope: 'department',
      },
    },
  });

  if (error) {
    return <ErrorHandler error={error} log="RatesOnDemandStepsStepTwo" />;
  }

  function onChange(newTargetKeys: string[]) {
    setTargetKeys(newTargetKeys);

    if (onOrgUnitsChange && data?.organizationUnits) {
      onOrgUnitsChange(
        data.organizationUnits.filter((unit: SelectOrganizationUnitType) =>
          newTargetKeys.includes(unit.id)
        )
      );
    }
  }

  const filterOption = filterOptionForColumns(
    tableColumns.map(({ key }) => key)
  );

  const titles = {
    leftSingular: 'Unit Available',
    rightSingular: 'Unit Selected',
    leftPlural: 'Units Available',
    rightPlural: 'Units Selected',
  };

  return (
    <TableTransfer<NormalizedDataType>
      loading={loading}
      className="update-steps__step-one"
      dataSource={normalizeData(data)}
      targetKeys={targetKeys}
      disabled={loading}
      onChange={onChange}
      columns={tableColumns}
      showSearch
      filterOption={filterOption}
      titles={titles}
    />
  );
}
